import React from 'react';
import { ComboboxProps } from '@economic/taco';
import Combobox from './primitives/Combobox';
import Accounts, { AccountModel } from '../../../views/Accounts';
import { t } from '../l10n/l10n';
import { useAccounts } from '../../hooks/masterdata/useAccounts';

export interface AccountComboboxProps {
    disableCreate?: boolean;
    isBlockedForDirectEntries?: boolean;
    onCreated?: Function;
    hideSummary?: boolean;
    [x: string]: any;
    modalDraggable?: boolean;
    showAllAccounts?: boolean;
    showAllAccountTypes?: boolean;
    allowHeadingClick?: boolean;
    width?: string;
    /**
     * Triggered when an account is selected from the modal. Normal account selection is handled by onChange.
     */
    onAccountSelectedFromModal?: (account: number) => void;
    showSaveAndApply?: boolean;
    onBlur?: ComboboxProps['onBlur'];
}

export default function AccountCombobox({
    disableCreate = false,
    isBlockedForDirectEntries,
    onCreated,
    hideSummary = false,
    onAccountSelectedFromModal,
    showAllAccountTypes = false,
    allowHeadingClick = false,
    width,
    showAllAccounts = false,
    onSearch,
    showSaveAndApply = false,
    ...props
}: AccountComboboxProps) {
    const { data: accounts } = useAccounts({
        showAllAccounts,
        showAllAccountTypes,
        // If heading accounts are not clickable in the Accounts modal,
        // they should also not be shown in the combobox dropdown options
        showHeadingAccounts: allowHeadingClick,
        isBlockedForDirectEntries,
        options: { useCache: props?.useCache },
    });

    function mapAccount(account: AccountModel) {
        return {
            data: account,
            text: `${account.number} - ${account.name}`,
            summary: hideSummary ? undefined : account.name,
            value: account.number,
        };
    }

    return (
        <Combobox
            {...props}
            aria-label={props['aria-label'] ?? t`MODAL_CHART_OF_ACCOUNTS_TITLE`}
            data={accounts.map(mapAccount)}
            modal={({ hide, onChange }) => (
                <Accounts
                    disableCreate={disableCreate}
                    modalDraggable={props.modalDraggable}
                    onCreated={account => {
                        onCreated?.(account);
                        onChange(account.number, account, true);
                        onAccountSelectedFromModal?.(account.number);
                        hide();
                    }}
                    onSelected={account => {
                        onChange(account.number, account);
                        onAccountSelectedFromModal?.(account.number);
                        hide();
                    }}
                    selectedAccountNumber={props.value}
                    showAllAccounts={showAllAccounts}
                    showAllAccountTypes={showAllAccountTypes}
                    allowHeadingClick={allowHeadingClick}
                    showSaveAndApply={showSaveAndApply}
                />
            )}
            modalSize="sm"
            className={width || 'w-36'}
        />
    );
}
